import { getByID } from '@adornis/baseql/operations/mongo.js';
import { countingTableView } from '@nsc/simple-lms/client/record-count-view.js';
import { ErmValue } from '@adornis/entity-relationship-management/client/details/erm-value.js';
import type { ERMEntityDefinition } from '@adornis/entity-relationship-management/client/types.js';
import { nothing } from 'lit';
import { countCustomers, subscribeCustomersPaginated } from '../../api/scoped-queries.js';
import { softDeleteCustomer } from '../../api/soft-delete.js';
import { SmartTeamsCustomer } from '../../db/index.js';
import { ProblemReportToggles } from '../components/problem-report-toggles.js';
import { ServiceNowCustomerName } from '../components/servicenow-customer-name.js';
import { ServiceNowClientPicker } from '../components/servicenow-pickers.js';
import { confirmAndSoftDelete } from './soft-delete.js';

export const customerManagerConfig: ERMEntityDefinition<typeof SmartTeamsCustomer> = {
  _class: SmartTeamsCustomer,
  label: 'Customers',
  icon: 'icon-[mdi--account-group-outline]',
  route: 'customers',
  detail: {
    operation: id => getByID(SmartTeamsCustomer, id),
    selectionSet: { _id: 1, name: 1, serviceNowClientId: 1, enabledProblemReports: 1 },
    title: e => e.name ?? 'New customer',
    sections: [
      {
        title: 'Customer',
        fields: [
          { fieldName: 'name', label: 'Name' },
          {
            fieldName: 'serviceNowClientId',
            label: 'ServiceNow Customer',
            render(props) {
              return ErmValue(
                'ServiceNow Customer',
                'sync',
                ServiceNowClientPicker({ ...props.form.field('serviceNowClientId') }),
              );
            },
          },
        ],
      },
      {
        title: 'Report a problem buttons',
        render: ({ entity }) =>
          entity
            ? ProblemReportToggles({
                value: entity.enabledProblemReports ?? [],
                onChange: enabledKeys => {
                  entity.enabledProblemReports = enabledKeys;
                },
              })
            : nothing,
      },
    ],
    onDelete: entity =>
      confirmAndSoftDelete({
        entityLabel: 'customer',
        name: entity.name,
        run: () => softDeleteCustomer(entity._id)(),
      }),
  },
  list: {
    operation: subscribeCustomersPaginated,
    selectionSet: { _id: 1, name: 1, serviceNowClientId: 1 },
    columns: [
      { fieldName: 'name', columnName: 'Name', isAlwaysVisible: true },
      {
        fieldName: 'serviceNowClientId',
        columnName: 'ServiceNow customer',
        selectionSet: { serviceNowClientId: 1 },
        renderItem: c => ServiceNowCustomerName({ clientId: c.serviceNowClientId }),
      },
    ],
    searchFields: ['name'],
    listViews: [countingTableView(filter => countCustomers(filter)())],
  },
  async onCreate() {
    return await new SmartTeamsCustomer({ name: 'New customer' }).create();
  },
};
